import i18next from 'i18next';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getUserSession } from '../../../utils/userSession';
import { UserSession } from '../../../common/interfaces/auth';
import { axiosClient as axios } from '../../../utils/axios_instance';
import { ExternalAccess, ExternalAccessState } from '../interfaces';
import { openSuccessNotification } from '../../../common/notification/Notification';
import { EXTERNAL_ACCESS_URL, START_INSTANCE_EC2_URL, STATUS_INSTANCE_EC2_URL, STOP_INSTANCE_EC2_URL } from '../../../config/config.api';

const initialState: ExternalAccessState = {
  isLoading: false,
  isCreating: false,
  externalAccess: [],
  showCreateModal: false,
  showEditModal: false,
};

export const externalAccessSlice = createSlice({
  name: 'externalAccess',
  initialState,
  reducers: {
    setExternalAccess: (state: ExternalAccessState, action: PayloadAction<ExternalAccess[]>) => {
      state.externalAccess = action.payload;
    },
    setIsLoading: (state: ExternalAccessState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsCreating: (state: ExternalAccessState, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },
    setShowCreateModal: (state: ExternalAccessState, action: PayloadAction<boolean>) => {
      state.showCreateModal = action.payload;
    },
    setShowEditModal: (state: ExternalAccessState, action: PayloadAction<boolean>) => {
      state.showEditModal = action.payload;
    },
    setSelectedExternalAccess: (state: ExternalAccessState, action: PayloadAction<ExternalAccess>) => {
      state.selectedExternalAccess = action.payload;
    },
  },
});

export const {
  setExternalAccess,
  setIsLoading,
  setIsCreating,
  setShowCreateModal,
  setShowEditModal,
  setSelectedExternalAccess,
} = externalAccessSlice.actions;

export const fetchExternalAccess = () => async (dispatch: Function) => {
  dispatch(setIsLoading(true));

  const userSession: UserSession = getUserSession();
  const params = {
    $limit: -1,
    companyId: userSession.companyId,
  };

  try {
    const response = await axios.get<ExternalAccess[]>(EXTERNAL_ACCESS_URL, { params });
    dispatch(setExternalAccess(response.data));
  } catch (e) {
    console.log(e?.response);
  }

  dispatch(setIsLoading(false));
};

export const saveExternalAccess = (props: {body: ExternalAccess; onSuccess: Function}) => async (dispatch: Function) => {
  const { body, onSuccess } = props;
  dispatch(setIsCreating(true));

  try {
    await axios.post<ExternalAccess>(EXTERNAL_ACCESS_URL, body);
    dispatch(fetchExternalAccess());
    onSuccess();
    openSuccessNotification(i18next.t('integrations.success'));
  } catch (e) {
    console.log(e?.response);
  }

  dispatch(setIsCreating(false));
};

export const updateExternalAccess = (props: {body: ExternalAccess; onSuccess: Function}) => async (dispatch: Function) => {
  const { body, onSuccess } = props;
  dispatch(setIsCreating(true));

  const url = `${EXTERNAL_ACCESS_URL}/${body._id}`;
  
  try {
    await axios.patch<ExternalAccess>(url, body);
    dispatch(fetchExternalAccess());
    onSuccess();
    openSuccessNotification(i18next.t('integrations.success'));
  } catch (e) {
    console.log(e?.response);
  }

  dispatch(setIsCreating(false));
};

export const fetchInstanceStatus = async () => {
  try {
    await axios.get(STATUS_INSTANCE_EC2_URL);
  } catch (error) {
    console.log(error?.response);
  }
};

export const startInstanceEc2 = async () => {
  try {
    await axios.post(START_INSTANCE_EC2_URL);
  } catch (error) {
    console.log(error?.response);
  }
};

export const stopInstanceEc2 = async () => {
  try {
    await axios.post(STOP_INSTANCE_EC2_URL);
  } catch (error) {
    console.log(error?.response);
  }
};

export default externalAccessSlice.reducer;
